import { useState, useEffect, useCallback } from 'react';
import { LocationResult } from '../types';

/**
 * Get the API base URL based on environment.
 */
function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  return 'https://unified-marketplace-api-production.up.railway.app/api/v1';
}

const API_BASE_URL = getApiBaseUrl();

interface GeolocationPosition {
  latitude: number;
  longitude: number;
}

interface UseGeolocationReturn {
  position: GeolocationPosition | null;
  locationName: string | null;
  loading: boolean;
  error: string | null;
  requestLocation: () => void;
  clearLocation: () => void;
}

const STORAGE_KEY = 'dryv_user_location';
const STORAGE_KEY_NAME = 'dryv_user_location_name';

/**
 * Custom hook to get user's geolocation and optionally convert to location name
 */
export function useGeolocation(): UseGeolocationReturn {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load saved location from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const savedName = localStorage.getItem(STORAGE_KEY_NAME);
      if (saved) {
        const parsed = JSON.parse(saved);
        setPosition(parsed);
        if (savedName) {
          setLocationName(savedName);
        }
      }
    } catch (e) {
      console.error('Failed to load saved location:', e);
    }
  }, []);

  // Reverse geocode coordinates to location name using backend API
  const reverseGeocode = useCallback(async (lat: number, lon: number): Promise<string | null> => {
    try {
      // Use backend API endpoint to avoid CORS issues
      const response = await fetch(
        `${API_BASE_URL}/locations/reverse-geocode?lat=${lat}&lon=${lon}&zoom=10`
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      // Return the location name from the backend response
      return data.location_name || null;
    } catch (e) {
      console.error('Reverse geocoding failed:', e);
      return null;
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (geoPosition) => {
        const newPosition: GeolocationPosition = {
          latitude: geoPosition.coords.latitude,
          longitude: geoPosition.coords.longitude,
        };

        setPosition(newPosition);
        
        // Save to localStorage
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newPosition));
        } catch (e) {
          console.error('Failed to save location:', e);
        }

        // Try to get location name
        const name = await reverseGeocode(newPosition.latitude, newPosition.longitude);
        if (name) {
          setLocationName(name);
          try {
            localStorage.setItem(STORAGE_KEY_NAME, name);
          } catch (e) {
            console.error('Failed to save location name:', e);
          }
        }

        setLoading(false);
      },
      (geoError) => {
        setLoading(false);
        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            setError('Location access denied. Please enable location permissions in your browser settings.');
            break;
          case geoError.POSITION_UNAVAILABLE:
            setError('Location information unavailable.');
            break;
          case geoError.TIMEOUT:
            setError('Location request timed out. Please try again.');
            break;
          default:
            setError('Failed to get your location. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  }, [reverseGeocode]);

  const clearLocation = useCallback(() => {
    setPosition(null);
    setLocationName(null);
    setError(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY_NAME);
    } catch (e) {
      console.error('Failed to clear location:', e);
    }
  }, []);

  return {
    position,
    locationName,
    loading,
    error,
    requestLocation,
    clearLocation,
  };
}

/**
 * Convert geolocation position to LocationResult format
 */
export function positionToLocationResult(
  position: GeolocationPosition | null,
  locationName: string | null
): LocationResult | null {
  if (!position) {
    return null;
  }

  return {
    name: locationName || `${position.latitude.toFixed(4)}, ${position.longitude.toFixed(4)}`,
    latitude: position.latitude.toString(),
    longitude: position.longitude.toString(),
  };
}

